import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import type { Person, CreatePersonDto } from '../../types/api';

const personSchema = z.object({
    fullName: z.string().min(1, 'Tên đầy đủ là bắt buộc'),
    givenName: z.string().optional(),
    gender: z.string(),
    birthDate: z.string().optional(),
    deathDate: z.string().optional(),
    notes: z.string().optional(),
    photoUrl: z.string().optional(),
    parentId1: z.string().optional(),
    parentId2: z.string().optional(),
});

type PersonFormData = z.infer<typeof personSchema>;

interface PersonFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CreatePersonDto) => void;
    person?: Person | null;
    isLoading?: boolean;
}

export function PersonForm({ isOpen, onClose, onSubmit, person, isLoading }: PersonFormProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<PersonFormData>({
        resolver: zodResolver(personSchema),
        defaultValues: person ? {
            fullName: person.fullName,
            givenName: person.givenName || '',
            gender: person.gender,
            birthDate: person.birthDate || '',
            deathDate: person.deathDate || '',
            notes: person.notes || '',
            photoUrl: person.photoUrl || '',
            parentId1: '',
            parentId2: '',
        } : {
            gender: 'MALE',
        },
    });

    const handleFormSubmit = (data: PersonFormData) => {
        const parentIds: string[] = [];
        if (data.parentId1) parentIds.push(data.parentId1);
        if (data.parentId2) parentIds.push(data.parentId2);

        onSubmit({
            fullName: data.fullName,
            givenName: data.givenName || undefined,
            gender: data.gender as 'MALE' | 'FEMALE' | 'OTHER',
            birthDate: data.birthDate || undefined,
            deathDate: data.deathDate || undefined,
            notes: data.notes || undefined,
            photoUrl: data.photoUrl || undefined,
            parentIds: parentIds.length > 0 ? parentIds : undefined,
        });
        reset();
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                                <div className="flex items-start justify-between mb-4">
                                    <Dialog.Title className="text-2xl font-bold text-gray-900">
                                        {person ? 'Chỉnh sửa thông tin' : 'Thêm người mới'}
                                    </Dialog.Title>
                                    <button
                                        onClick={onClose}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                                    {/* Full Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Tên đầy đủ *
                                        </label>
                                        <input
                                            {...register('fullName')}
                                            className="input"
                                            placeholder="Nguyễn Văn A"
                                        />
                                        {errors.fullName && (
                                            <p className="mt-1 text-sm text-red-600">{String(errors.fullName.message)}</p>
                                        )}
                                    </div>

                                    {/* Given Name & Gender */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Tên gọi
                                            </label>
                                            <input
                                                {...register('givenName')}
                                                className="input"
                                                placeholder="A"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Giới tính *
                                            </label>
                                            <select {...register('gender')} className="input">
                                                <option value="MALE">Nam</option>
                                                <option value="FEMALE">Nữ</option>
                                                <option value="OTHER">Khác</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Birth & Death Date */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Ngày sinh
                                            </label>
                                            <input
                                                {...register('birthDate')}
                                                type="date"
                                                className="input"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Ngày mất
                                            </label>
                                            <input
                                                {...register('deathDate')}
                                                type="date"
                                                className="input"
                                            />
                                        </div>
                                    </div>

                                    {/* Photo URL */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            URL ảnh
                                        </label>
                                        <input
                                            {...register('photoUrl')}
                                            type="url"
                                            className="input"
                                            placeholder="https://example.com/photo.jpg"
                                        />
                                        {errors.photoUrl && (
                                            <p className="mt-1 text-sm text-red-600">{String(errors.photoUrl.message)}</p>
                                        )}
                                    </div>

                                    {/* Notes */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Ghi chú
                                        </label>
                                        <textarea
                                            {...register('notes')}
                                            rows={4}
                                            className="input"
                                            placeholder="Thông tin về cuộc đời, sự nghiệp..."
                                        />
                                    </div>

                                    {/* Parent IDs */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                ID Cha
                                            </label>
                                            <input
                                                {...register('parentId1')}
                                                className="input"
                                                placeholder="UUID của cha"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                ID Mẹ
                                            </label>
                                            <input
                                                {...register('parentId2')}
                                                className="input"
                                                placeholder="UUID của mẹ"
                                            />
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-3 pt-4 border-t">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="btn btn-secondary flex-1"
                                            disabled={isLoading}
                                        >
                                            Hủy
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary flex-1"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Đang lưu...' : person ? 'Cập nhật' : 'Thêm mới'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
